import React, { useContext, useEffect, useRef, type FC } from 'react';
import { useIntl } from 'react-intl-next';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { Stack, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import UpsellModal from '@atlassian/upsell-modal';

import { ConfluenceEdition, useSessionData } from '@confluence/session-data';
import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';
import {
	AUA_MODAL_EXPERIENCE,
	ExperienceFailure,
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { ScreenEvent } from '@confluence/analytics';
import {
	useCanUpgradeAndPayForProductUser,
	useCanAddBillingForTrialProductUser,
} from '@confluence/experiment-more-product-users-can-upgrade-pay';

import {
	getModalEventName,
	AUTO_UPGRADE_AWARENESS_MODAL_SOURCE,
	AUTO_UPGRADE_AWARENESS_MODAL_TEST_ID,
} from './hooks/analytics-handlers/analyticsConstants';
import { useGetUserCount } from './hooks/useGetUserCount';
import { useAUATranslations } from './hooks/useAUATranslations';
import { useModalEventHandler } from './hooks/analytics-handlers/useModalEventHandler';
import { useDaysLeftTrial } from './hooks/useDaysLeftTrial';
import { useGetStorageUsageData } from './hooks/useGetStorageUsageData';
import { useGetUserAvatarData } from './hooks/useGetUserAvatarData';
import { useModalCTALinkFree } from './hooks/useModalCTALinkFree';
import { useModalCTALinkTrial } from './hooks/useModalCTALinkTrial';
import { GuestAccess } from './carousel/GuestAccess';
import { UnlimitedUserLimit } from './carousel/UnlimitedUserLimit';
import { PageRestrictions } from './carousel/PageRestrictions';
import { PageInsights } from './carousel/PageInsights';
import { SubHeading } from './contentSection/SubHeading';
import { StorageUsageSection } from './contentSection/StorageUsageSection';
import { UserAvatarSection } from './contentSection/UserAvatarSection';

type AutoUpgradeAwarenessModalProps = {
	touchpointId: string;
	closeModal: () => void;
};

const contentSectionStyles = xcss({ paddingTop: 'space.200' });

export const Modal: FC<AutoUpgradeAwarenessModalProps> = ({ touchpointId, closeModal }) => {
	const { edition } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const hasExperienceTrackerStartedRef = useRef<Boolean>(false);

	if (!hasExperienceTrackerStartedRef.current) {
		experienceTracker.start({
			name: AUA_MODAL_EXPERIENCE,
		});

		hasExperienceTrackerStartedRef.current = true;
	}

	const {
		analyticsAttributes: mpuAnalyticsAttributesFree,
		canUpgradeAndPayForProductUser: isEligibleForMpuFree,
		loading: mpuFreeLoading,
		error: mpuFreeError,
	} = useCanUpgradeAndPayForProductUser();

	const {
		analyticsAttributes: mpuAnalyticsAttributesStandard,
		canAddBillingForTrial: isEligibleForMpuStandard,
		loading: mpuTrialLoading,
		commerceError: mpuTrialError,
	} = useCanAddBillingForTrialProductUser();

	const isEligibleForMpu = isEligibleForMpuFree || isEligibleForMpuStandard;
	const mpuAnalyticsAttributes = isEligibleForMpuFree
		? mpuAnalyticsAttributesFree
		: mpuAnalyticsAttributesStandard;

	const {
		loading: ctaLinkFreeLoading,
		error: ctaLinkFreeError,
		startFreeTrialCtaLink,
	} = useModalCTALinkFree(edition !== ConfluenceEdition.FREE);

	const {
		loading: ctaLinkTrialLoading,
		error: ctaLinkTrialError,
		addPaymentDetailsCtaLink,
	} = useModalCTALinkTrial(edition === ConfluenceEdition.FREE);

	const { daysLeft, error: daysLeftError, loading: daysLeftLoading } = useDaysLeftTrial();

	const { eventHandler } = useModalEventHandler();

	const intl = useIntl();

	const { userCount, error: userCountError, loading: userCountLoading } = useGetUserCount();

	const {
		dataUsed,
		error: storageUsageError,
		loading: storageUsageLoading,
	} = useGetStorageUsageData();

	const {
		userAvatarData,
		error: userAvatarDataError,
		loading: userAvatarDataLoading,
	} = useGetUserAvatarData();

	const {
		modalTranslations,
		modalPrimaryCTA,
		modalCloseCTA,
		modalCarousel,
		modalStorageUsage,
		modalUserAvatar,
	} = useAUATranslations();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'exposed',
				actionSubject: 'autoUpgradeAwarenessModal',
				attributes: {
					growthFeature: 'autoUpgradeAwarenessModal',
					edition,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, edition]);

	const carouselItems = [
		[
			intl.formatMessage(modalCarousel.unlimitedUserLimit),
			<UnlimitedUserLimit key="Unlimited user limit" />,
		],
		[intl.formatMessage(modalCarousel.pageInsights), <PageInsights key="Page ingights" />],
		[
			intl.formatMessage(modalCarousel.pageRestrictions),
			<PageRestrictions key="Page restrictions" />,
		],
		[intl.formatMessage(modalCarousel.guestAccess), <GuestAccess key="Guest access" />],
	].map(([caption, component]) => ({
		caption: caption as string,
		component: {
			dark: component as JSX.Element,
			light: component as JSX.Element,
		},
	}));

	const loading =
		userCountLoading ||
		mpuFreeLoading ||
		mpuTrialLoading ||
		ctaLinkFreeLoading ||
		ctaLinkTrialLoading ||
		daysLeftLoading ||
		storageUsageLoading ||
		userAvatarDataLoading;
	const error =
		userCountError ||
		mpuFreeError ||
		mpuTrialError ||
		ctaLinkFreeError ||
		ctaLinkTrialError ||
		daysLeftError ||
		storageUsageError ||
		userAvatarDataError;

	if (error) {
		const errorMessages = {
			userCountError: userCountError?.message,
			mpuFreeError: mpuFreeError?.message,
			mpuTrialError: mpuTrialError?.message,
			ctaLinkFreeError: ctaLinkFreeError?.message,
			ctaLinkTrialError: ctaLinkTrialError?.message,
			daysLeftError: daysLeftError?.message,
			storageUsageError: storageUsageError?.message,
			userAvatarDataError: userAvatarDataError?.message,
		};

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'failed',
				actionSubject: 'autoUpgradeAwarenessModal',
				source: 'autoUpgradeAwarenessModal',
				attributes: {
					growthFeature: 'autoUpgradeAwarenessModal',
					edition,
					error: errorMessages,
				},
			},
		}).fire();

		return (
			<ExperienceFailure
				name={AUA_MODAL_EXPERIENCE}
				error={error}
				attributes={{
					edition,
					...errorMessages,
				}}
			/>
		);
	}
	if (loading) {
		return null;
	}
	return (
		<ModalTransition>
			<>
				<ScreenEvent
					name={getModalEventName(userCount, edition)}
					id={AUTO_UPGRADE_AWARENESS_MODAL_SOURCE}
					attributes={{
						touchpointId,
						userCount,
						dataUsed,
						...(isEligibleForMpu && mpuAnalyticsAttributes),
					}}
				/>
				<UpsellModal
					testId={AUTO_UPGRADE_AWARENESS_MODAL_TEST_ID}
					height={600}
					width={1000}
					header={intl.formatMessage(modalTranslations.header)}
					carousel={{
						items: carouselItems,
					}}
					cta={{
						url:
							edition === ConfluenceEdition.FREE ? startFreeTrialCtaLink : addPaymentDetailsCtaLink,
						isLoading: false,
						message: intl.formatMessage(modalPrimaryCTA.text),
						onClick: () => {
							eventHandler({
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: edition === 'FREE' ? 'startFreeTrial' : 'addPaymentDetails',
								mpuAnalyticsAttributes,
								userCount,
							});
						},
					}}
					close={{
						showCloseButton: false,
						message: intl.formatMessage(modalCloseCTA.text),
						onClose: (trigger) => {
							eventHandler({
								action: trigger,
								actionSubject: AUTO_UPGRADE_AWARENESS_MODAL_SOURCE,
								mpuAnalyticsAttributes,
								userCount,
							});
							closeModal();
						},
					}}
				>
					<Stack space="space.400" xcss={contentSectionStyles}>
						<SubHeading
							text={modalTranslations.subHeader}
							daysLeftTrial={daysLeft}
							edition={edition}
						/>
						<UserAvatarSection
							text={modalUserAvatar}
							userCount={userCount}
							edition={edition}
							userAvatarData={userAvatarData}
						/>
						<StorageUsageSection
							text={modalStorageUsage}
							storageUsage={dataUsed}
							edition={edition}
						/>
					</Stack>
				</UpsellModal>
				<ExperienceSuccess name={AUA_MODAL_EXPERIENCE} />
			</>
		</ModalTransition>
	);
};

export const AutoUpgradeAwarenessModal = withTransparentErrorBoundary({
	attribution: Attribution.GROWTH_LUNA,
})(Modal);
